// frontend/src/components/VIPPatientAnalytics.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosClient from "../api/axiosClient";
import {
  Card,
  Row,
  Col,
  Form,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";

import { Line, Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  ChartDataLabels
);

// ----------------------- UI constants & styles -----------------------
const glass = {
  background: "rgba(255,255,255,0.20)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: "1px solid rgba(255,255,255,0.28)",
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  borderRadius: 12,
};

const SUMMARY_MIN_HEIGHT = 140;
const CHART_HEIGHT = 420;

const COLORS = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#7FB77E",
  "#E15FED",
  "#76D7C4",
  "#F1948A",
];

// ----------------------- helper functions -----------------------
const findKey = (obj, candidates) => {
  if (!obj) return null;
  const keys = Object.keys(obj);
  for (const cand of candidates) {
    const normalized = cand.toLowerCase().replace(/\s|_/g, "");
    const found = keys.find(
      (k) => k.toLowerCase().replace(/\s|_/g, "") === normalized
    );
    if (found) return found;
  }
  return null;
};

const parseNumber = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  const s = String(v).replace(/[^0-9.-]/g, "");
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
};

const parseDate = (v) => {
  if (!v && v !== 0) return null;
  if (v instanceof Date && !isNaN(v.getTime())) return v;

  if (typeof v === "number") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  let s = String(v).trim();
  const msMatch = s.match(/\/Date\((\d+)\)\//);
  if (msMatch) return new Date(Number(msMatch[1]));
  const iso = new Date(s);
  if (!isNaN(iso.getTime())) return iso;
  const alt = new Date(s.replace(" ", "T"));
  return !isNaN(alt.getTime()) ? alt : null;
};

const toLocalDateOnly = (d) =>
  d instanceof Date ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null;

const formatCurrency = (n) =>
  "₱" +
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatNumber = (n) => Number(n || 0).toLocaleString("en-US");

// ----------------------- COMPONENT -----------------------
const VIPPatientAnalytics = ({ currentUser }) => {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // detected keys
  const [detectedKeys, setDetectedKeys] = useState({});
  const { storeKey, patientKey, deptKey, sexKey, ageKey, txnKey, dateKey, descKey, uomKey, qtyKey, amountKey } = detectedKeys;

  // filters
  const [selectedStore, setSelectedStore] = useState("All");
  const [selectedDept, setSelectedDept] = useState("All");
  const [patientInput, setPatientInput] = useState(""); // search/datalist
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [topN, setTopN] = useState(10);

  // ----------------------- load data and detect keys -----------------------
  useEffect(() => {
    if (!currentUser?.userId) {
      setError("User not identified.");
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const res = await axiosClient.get(`/manual-reports/vip/${currentUser.userId}?report_type=consumption_by_patient_served`);
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];

        setRawRows(data || []);

        if (data && data.length) {
          const sample = data[0];

          const storeK = findKey(sample, [
            "Store",
            "Store_Code",
            "Store Code",
            "Branch",
            "Location",
            "Store_Name",
            "Store Name"
          ]);
          const patientK = findKey(sample, [
            "Patient_Name",
            "Patient Name",
            "Customer_Name",
            "Customer Name",
            "FullName",
            "Name",
          ]);
          const deptK = findKey(sample, ["Department", "Dept", "DepartmentName"]);
          const sexK = findKey(sample, ["Sex", "Gender", "PatientSex"]);
          const ageK = findKey(sample, ["Age", "Patient_Age", "Age_Years"]);
          const txnK = findKey(sample, [
            "Transaction_No",
            "Transaction_No.",
            "Transaction No.",
            "Transaction_Number",
            "Transaction Number",
            "Transaction_Receipt_No",
            "Transaction_Receipt_No.",
            "Transaction Receipt No",
            "Transaction Receipt No.",
            "Transaction_Receipt_Number",
            "Transaction Receipt Number",
            "Receipt_No",
            "Receipt_No.",
            "Receipt_Number",
            "Receipt Number",
            "Reference_No",
            "Reference_No.",
            "Reference No",
            "Reference No.",
            "Reference_Number",
            "Reference Number",
            "Doc_No",
            "Document No",
          ]);
          const dateK = findKey(sample, [
            "Dispense Date",
            "Dispense_Date",
            "Date",
            "Posting Date",
            "Transaction Date",
            "Entry Date",
          ]);
          const descK = findKey(sample, [
            "Description",
            "Item Description",
            "Product Name",
            "Item",
            "Product",
          ]);
          const uomK = findKey(sample, ["Base UOM", "Unit", "Unit_of_Measure", "UOM"]);
          const qtyK = findKey(sample, ["Quantity", "Qty", "QTY"]);
          const amountK = findKey(sample, ["Amount", "Line Amount", "Total Amount", "Total_Amount"]);

          setDetectedKeys({
            storeKey: storeK,
            patientKey: patientK,
            deptKey: deptK,
            sexKey: sexK,
            ageKey: ageK,
            txnKey: txnK,
            dateKey: dateK,
            descKey: descK,
            uomKey: uomK,
            qtyKey: qtyK,
            amountKey: amountK,
          });

          // default date range Last 30 days if dateK detected
          if (dateK) {
            const allDates = data.map((r) => parseDate(r[dateK])).filter(Boolean).sort((a, b) => a - b);
            if (allDates.length) {
              const max = allDates[allDates.length - 1];
              const min = new Date(max);
              min.setDate(min.getDate() - 30);
              setStartDate(min.toISOString().split("T")[0]);
              setEndDate(max.toISOString().split("T")[0]);
            }
          }
        }

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError("Failed to load patient manual report.");
        setLoading(false);
      }
    };

    load();
  }, [currentUser]);

  // ----------------------- derived lists for filters -----------------------
  const allStores = useMemo(() => {
    if (!storeKey) return ["All"];
    const s = new Set(rawRows.map((r) => r[storeKey]).filter(Boolean));
    return ["All", ...Array.from(s).sort()];
  }, [rawRows, storeKey]);

  const allDepts = useMemo(() => {
    if (!deptKey) return ["All"];
    const s = new Set(rawRows.map((r) => r[deptKey]).filter(Boolean));
    return ["All", ...Array.from(s).sort()];
  }, [rawRows, deptKey]);

  const allPatients = useMemo(() => {
    if (!patientKey) return [];
    const s = new Set(rawRows.map((r) => (r[patientKey] ? String(r[patientKey]).trim() : "")).filter(Boolean));
    return Array.from(s).sort();
  }, [rawRows, patientKey]);

  // ----------------------- apply store/department/patient/date filters -----------------------
  const filteredRows = useMemo(() => {
    let rows = rawRows;

    if (storeKey && selectedStore && selectedStore !== "All") {
      rows = rows.filter((r) => String(r[storeKey] || "") === String(selectedStore));
    }
    if (deptKey && selectedDept && selectedDept !== "All") {
      rows = rows.filter((r) => String(r[deptKey] || "") === String(selectedDept));
    }

    // patientInput can be empty (All) or partial search OR exact from datalist
    if (patientInput) {
      const q = String(patientInput).trim().toLowerCase();
      // filter any row where patient name contains q
      rows = rows.filter((r) => {
        const val = String(r[patientKey] || "").toLowerCase();
        return val.includes(q);
      });
    }

    if (startDate && endDate && dateKey) {
      const s = toLocalDateOnly(parseDate(startDate));
      const e = toLocalDateOnly(parseDate(endDate));
      if (s && e) {
        rows = rows.filter((r) => {
          const d = parseDate(r[dateKey]);
          if (!d) return false;
          const dl = toLocalDateOnly(d);
          return dl >= s && dl <= e;
        });
      }
    }

    return rows;
  }, [
    rawRows,
    storeKey,
    selectedStore,
    deptKey,
    selectedDept,
    patientKey,
    patientInput,
    startDate,
    endDate,
    dateKey,
  ]);

  // ----------------------- parsed rows (stable mapping) -----------------------
  const parsed = useMemo(
    () =>
      filteredRows.map((r) => ({
        store: storeKey ? r[storeKey] : undefined,
        patient: patientKey ? r[patientKey] : undefined,
        dept: deptKey ? r[deptKey] : undefined,
        sex: sexKey ? r[sexKey] : undefined,
        age: ageKey ? parseNumber(r[ageKey]) : undefined,
        txn: txnKey ? r[txnKey] : undefined,
        date: dateKey ? parseDate(r[dateKey]) : undefined,
        desc: descKey ? r[descKey] : undefined,
        uom: uomKey ? r[uomKey] : undefined,
        qty: qtyKey ? parseNumber(r[qtyKey]) : 0,
        amount: amountKey ? parseNumber(r[amountKey]) : 0,
        raw: r,
      })),
    [filteredRows, storeKey, patientKey, deptKey, sexKey, ageKey, txnKey, dateKey, descKey, uomKey, qtyKey, amountKey]
  );

  // ----------------------- summary metrics -----------------------
  const transactionCount = parsed.length;
  const totalAmount = parsed.reduce((acc, p) => acc + (p.amount || 0), 0);

  const storeSummary = useMemo(() => {
    return Object.values(
      parsed.reduce((acc, p) => {
        const s = p.store || "Unknown";
        if (!acc[s]) acc[s] = { store: s, qty: 0, amount: 0, count: 0 };
        acc[s].qty += p.qty || 0;
        acc[s].amount += p.amount || 0;
        acc[s].count += 1;
        return acc;
      }, {})
    );
  }, [parsed]);

  const highestQtyStore = storeSummary.slice().sort((a, b) => b.qty - a.qty)[0] || { store: "-", qty: 0 };
  const highestAmountStore = storeSummary.slice().sort((a, b) => b.amount - a.amount)[0] || { store: "-", amount: 0 };

  // ----------------------- daily sales (line chart) grouped by date -----------------------
  const dailySales = useMemo(() => {
    const map = {};
    parsed.forEach((p) => {
      if (!p.date) return;
      const iso = p.date.toISOString().split("T")[0];
      if (!map[iso]) map[iso] = { amount: 0, count: 0 };
      map[iso].amount += p.amount || 0;
      map[iso].count += 1; // count transactions/rows for that date
    });
    return Object.entries(map)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([date, obj]) => ({
        date,
        label: new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        amount: obj.amount,
        count: obj.count,
      }));
  }, [parsed]);

  // line chart data => dual axis
  const lineData = useMemo(() => {
    return {
      labels: dailySales.map((d) => d.label),
      datasets: [
        {
          label: "Amount (₱)",
          data: dailySales.map((d) => d.amount),
          yAxisID: "y",
          borderColor: "#1E90FF",
          backgroundColor: "rgba(30,144,255,0.25)",
          fill: true,
          tension: 0.35,
        },
        {
          label: "Count (#)",
          data: dailySales.map((d) => d.count),
          yAxisID: "y1",
          borderColor: "#FF6384",
          backgroundColor: "rgba(255,99,132,0.25)",
          fill: true,
          tension: 0.35,
        },
      ],
    };
  }, [dailySales]);

  // ----------------------- Age bracket pie -----------------------
  const ageBrackets = useMemo(() => {
    const counts = { Infant: 0, Adult: 0, "Senior Citizen": 0, Unspecified: 0 };
    parsed.forEach((p) => {
      const age = p.age;
      if (age === undefined || age === null || Number.isNaN(age) || age === 0) {
        counts.Unspecified++;
      } else if (age >= 0 && age <= 12) counts.Infant++;
      else if (age >= 13 && age <= 59) counts.Adult++;
      else if (age >= 60) counts["Senior Citizen"]++;
      else counts.Unspecified++;
    });
    return counts;
  }, [parsed]);

  const pieData = useMemo(() => {
    const labels = ["Infant", "Adult", "Senior Citizen", "Unspecified"];
    const data = labels.map((l) => ageBrackets[l] || 0);
    return {
      labels,
      datasets: [{ data, backgroundColor: COLORS.slice(0, labels.length) }],
    };
  }, [ageBrackets]);

  // ----------------------- Sex bar chart -----------------------
  const sexCounts = useMemo(() => {
    const counts = { Male: 0, Female: 0, Unspecified: 0 };
    parsed.forEach((p) => {
      const s = p.sex ? String(p.sex).trim().toLowerCase() : "";
      if (!s) counts.Unspecified++;
      else if (["m", "male"].includes(s)) counts.Male++;
      else if (["f", "female"].includes(s)) counts.Female++;
      else counts.Unspecified++;
    });
    return counts;
  }, [parsed]);

  const sexBarData = useMemo(() => {
    const labels = ["Male", "Female", "Unspecified"];
    const data = labels.map((l) => sexCounts[l] || 0);
    return {
      labels,
      datasets: [
        {
          label: "Count",
          data,
          backgroundColor: ["#36A2EB", "#FF6384", "#C9CBCF"],
          borderColor: ["#1E90FF", "#FF6384", "#BFC9CA"],
          borderWidth: 1,
        },
      ],
    };
  }, [sexCounts]);

  // ----------------------- Group parsed rows by patient -> txn -> items (for table) -----------------------
  const grouped = useMemo(() => {
    // We assume each row is a line item and has a transaction number
    const byPatient = {};
    parsed.forEach((r) => {
      const pname = (r.patient || "UNKNOWN").toString();
      const txn = r.txn ? r.txn.toString() : "__NO_TXN__:" + JSON.stringify(r.raw).slice(0, 8);
      if (!byPatient[pname]) byPatient[pname] = {};
      if (!byPatient[pname][txn]) byPatient[pname][txn] = { date: r.date, items: [], total: 0 };
      byPatient[pname][txn].items.push({
        desc: r.desc || "",
        uom: r.uom || "",
        qty: r.qty || 0,
        amount: r.amount || 0,
      });
      byPatient[pname][txn].total += r.amount || 0;
    });

    // convert to array with sorted patient names and txns sorted by date desc
    const out = Object.keys(byPatient)
      .sort((a, b) => a.localeCompare(b))
      .map((pname) => {
        const txnsObj = byPatient[pname];
        const txns = Object.keys(txnsObj)
          .map((t) => ({ txnNo: t, date: txnsObj[t].date, items: txnsObj[t].items, total: txnsObj[t].total }))
          .sort((a, b) => {
            const da = a.date ? new Date(a.date) : new Date(0);
            const db = b.date ? new Date(b.date) : new Date(0);
            return db - da; // newest first
          });
        return { patient: pname, txns };
      });

    return out;
  }, [parsed]);

  // ----------------------- early returns & checks -----------------------
  if (loading) return <div className="text-center my-4"><Spinner animation="border" /></div>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!rawRows.length) return <Alert variant="info">No patient manual report data found.</Alert>;
  if (!dateKey) return <Alert variant="warning">No Dispense Date column detected in the uploaded report.</Alert>;

  // ----------------------- Chart options -----------------------
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            if (ctx.dataset.yAxisID === "y") {
              return `Amount: ${formatCurrency(ctx.parsed.y ?? ctx.parsed)}`;
            }
            return `Count: ${Math.round(ctx.parsed.y ?? ctx.parsed)}`;
          },
        },
      },
    },
    scales: {
      y: {
        type: "linear",
        position: "left",
        ticks: { callback: (v) => formatCurrency(v) },
      },
      y1: {
        type: "linear",
        position: "right",
        grid: { drawOnChartArea: false },
        ticks: { callback: (v) => Math.round(v) },
      },
    },
  };

  const barOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        ticks: { callback: (v) => formatNumber(v) },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" } },
  };

  // ----------------------- RENDER -----------------------
  return (
    <div className="mb-5">
      {/* FILTERS (glass) */}
      <Card className="p-4 mb-4" style={glass}>
        <h5 className="mb-3">Filters</h5>
        <Row className="g-3 justify-content-center">
          <Col md={4}>
            <Form.Label>Store</Form.Label>
            <Form.Select value={selectedStore} onChange={(e) => setSelectedStore(e.target.value)}>
              {allStores.map((s) => <option key={s} value={s}>{s}</option>)}
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label>Department</Form.Label>
            <Form.Select value={selectedDept} onChange={(e) => setSelectedDept(e.target.value)}>
              {allDepts.map((d) => <option key={d} value={d}>{d}</option>)}
            </Form.Select>
          </Col>

          <Col md={2}>
            <Form.Label>Start Date</Form.Label>
            <Form.Control type="date" value={startDate} max={endDate || ""} onChange={(e) => setStartDate(e.target.value)} />
          </Col>

          <Col md={2}>
            <Form.Label>End Date</Form.Label>
            <Form.Control type="date" value={endDate} min={startDate || ""} onChange={(e) => setEndDate(e.target.value)} />
          </Col>
        </Row>
      </Card>

      {/* SUMMARY CARDS */}
      <Row className="g-4 mb-4 text-center justify-content-center">
        <Col md={3}>
          <Card className="p-3 h-100 justify-content-center" style={{ ...glass, minHeight: SUMMARY_MIN_HEIGHT }}>
            <h6>No. of Transactions</h6>
            <h2 className="fw-bold">{formatNumber(transactionCount)}</h2>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="p-3 h-100 justify-content-center" style={{ ...glass, minHeight: SUMMARY_MIN_HEIGHT }}>
            <h6>Total Running Amount</h6>
            <h2 className="fw-bold">{formatCurrency(totalAmount)}</h2>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="p-3 h-100" style={{ ...glass, minHeight: SUMMARY_MIN_HEIGHT }}>
            <h6>Store Summary</h6>
            <div>
              <div><strong>Highest Qty:</strong> {highestQtyStore.store} ({formatNumber(highestQtyStore.qty)})</div>
              <div><strong>Highest Amount:</strong> {highestAmountStore.store} ({formatCurrency(highestAmountStore.amount)})</div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* CHARTS ROW (6 / 3 / 3) */}
      <Row className="g-4">
        <Col md={6}>
          <Card className="p-3 h-100" style={{ ...glass, minHeight: CHART_HEIGHT }}>
            <h6 className="text-center mb-2">Sales Per Day</h6>
            <div style={{ height: CHART_HEIGHT }}>
              <Line data={lineData} options={lineOptions} />
            </div>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="p-3 h-100" style={{ ...glass, minHeight: CHART_HEIGHT }}>
            <div className="d-flex justify-content-between mb-2">
              <h6 className="mb-0">Sex Category</h6>
              <Form.Select size="sm" value={topN} onChange={(e) => setTopN(Number(e.target.value))} style={{ width: 80 }}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
              </Form.Select>
            </div>
            <div style={{ height: CHART_HEIGHT }}>
              <Bar data={sexBarData} options={barOptions} />
            </div>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="p-3 h-100 d-flex flex-column align-items-center" style={{ ...glass, minHeight: CHART_HEIGHT }}>
            <h6 className="text-center mb-2">Age Bracket</h6>
            <div style={{ height: CHART_HEIGHT + 40, width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Pie data={pieData} options={pieOptions} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* DATA TABLE (grouped) */}
          

      <Card className="shadow-sm mt-4" style={glass}>
        <Card.Body>

          <h5 className="mb-3 text-center">Patient Transactions (grouped)</h5>
          <Col md={4}>
            <Form.Label>Patient Name (search)</Form.Label>
            {/* datalist + input for a simple searchable select */}
            <Form.Control
              type="text"
              list="patients-list"
              placeholder="Type patient name or choose from list"
              value={patientInput}
              onChange={(e) => setPatientInput(e.target.value)}
            />
            <datalist id="patients-list">
              {allPatients.map((p) => <option key={p} value={p} />)}
            </datalist>
          </Col>

          <div style={{ maxHeight: 520, overflowY: "auto", border: "1px solid #ddd"}}>
            <div style={{ overflowX: "auto", width: "100%" }}>
              <Table hover size="sm" style={{ tableLayout: "fixed", width: "100%" }}>
                {/* <thead style={{ position: "sticky", top: 0, background: "rgba(255,255,255,0.6)", zIndex: 2 }}> */}
                <thead className="sticky-header">
                  <tr>
                    <th style={{ width: 420 }}>Name - Transaction Number - Date & Time</th>
                    <th style={{ width: 180 }}>Base UOM</th>
                    <th style={{ width: 120 }}>Quantity</th>
                    <th style={{ width: 120 }}>Amount</th>
                    <th style={{ width: 160 }}>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map((p) => (
                    <React.Fragment key={p.patient}>
                      {/* Patient name as a header row (spans table) */}
                      <tr style={{ background: "rgba(0,0,0,0.03)" }}>
                        <td colSpan={5} style={{ fontWeight: 600 }}>{p.patient}</td>
                      </tr>

                      {p.txns.map((t) => (
                        <React.Fragment key={t.txnNo + String(t.date || "")}>
                          {/* Transaction header */}
                          <tr style={{ background: "rgba(0,0,0,0.02)" }}>
                            <td style={{ paddingLeft: 12 }}>
                              <strong>{t.txnNo}</strong>
                              {t.date ? ` · ${new Date(t.date).toLocaleString()}` : ""}
                            </td>
                            <td></td><td></td><td></td><td style={{ fontWeight: 700 }}>{formatCurrency(t.total)}</td>
                          </tr>

                          {/* items */}
                          {t.items.map((it, i) => (
                            <tr key={i}>
                              <td style={{ paddingLeft: 24 }}>{it.desc}</td>
                              <td>{it.uom}</td>
                              <td>{formatNumber(it.qty)}</td>
                              <td>{formatCurrency(it.amount)}</td>
                              {/* keep columns aligned; last column already used for amount */}
                            </tr>
                          ))}

                        </React.Fragment>
                      ))}

                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

        </Card.Body>
      </Card>
    </div>
  );
};

export default VIPPatientAnalytics;
